import { useEffect, useMemo } from "react";

import { useAppSelector } from "@/hooks/redux";
import { openReplayService } from "@/Services/OpenReplay";

export const useOpenReplay = () => {
    const { id, role, lastName, firstName, team, phone } = useAppSelector(
        (state) => state.user,
    );

    const metadata = useMemo(
        () => ({
            role: String(role ?? ""),
            name: `${firstName ?? ""} ${lastName ?? ""}`.trim(),
            region: String(team?.mainRegionName ?? ""),
            phone: String(phone ?? ""),
        }),
        [firstName, lastName, phone, role, team?.mainRegionName],
    );

    useEffect(() => {
        if (!id) return;

        const init = async () => {
            if (!openReplayService.isColdStarted()) {
                await openReplayService.coldStart(String(id), metadata);
                return;
            }

            openReplayService.setUser(String(id));

            Object.entries(metadata).forEach(([key, value]) => {
                openReplayService.setMetadata(key, value);
            });
        };

        void init();
    }, [id, metadata]);

    useEffect(() => {
        const handleError = (event: ErrorEvent) => {
            void openReplayService.startOnError("window_error", {
                message: String(event.message ?? ""),
                filename: String(event.filename ?? ""),
                lineno: String(event.lineno ?? ""),
                colno: String(event.colno ?? ""),
                route: window.location.pathname,
            });
        };

        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            const reason =
                event.reason instanceof Error
                    ? event.reason.message
                    : String(event.reason ?? "");

            void openReplayService.startOnError("window_unhandledrejection", {
                message: reason,
                route: window.location.pathname,
            });
        };

        window.addEventListener("error", handleError);
        window.addEventListener("unhandledrejection", handleUnhandledRejection);

        return () => {
            window.removeEventListener("error", handleError);
            window.removeEventListener(
                "unhandledrejection",
                handleUnhandledRejection,
            );
        };
    }, []);
};
